import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, QrCode, Copy, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickSignInSettings = () => {
  const navigate = useNavigate();
  const { agent } = useAuth();
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agent) {
      navigate('/login');
      return;
    }
    fetchQRCode();
  }, [agent]);

  const fetchQRCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/quick-signin/qr/${agent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrData(response.data);
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
      toast.error('Failed to load Quick Sign-In QR code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrData.qr_code}`;
    link.download = `quick-signin-qr-${agent.name.replace(/\s+/g, '-')}.png`;
    link.click();
    toast.success('QR code downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-3 md:py-4 px-4 md:px-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/settings')}
              variant="ghost"
              size="sm"
              className="text-stone-600 hover:text-[#1A4D2E]"
            >
              <ArrowLeft size={18} className="mr-2" />
              Back
            </Button>
            <h1 className="text-xl md:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
              Quick Sign-In
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        {/* Info Card */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <h2 className="text-lg font-semibold text-blue-900 mb-2">What is Quick Sign-In?</h2>
          <p className="text-sm text-blue-800">
            Quick Sign-In provides a simplified way for visitors to leave their contact information. 
            When they scan this QR code, they'll see a short form asking for just their name, email, phone, and an optional note.
            All leads captured this way are marked as <strong>private</strong> and will only be visible to you.
          </p>
        </Card>

        {/* QR Code Section */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <QrCode className="text-[#1A4D2E]" size={24} />
            <h2 className="text-lg font-semibold text-stone-900">Your Quick Sign-In QR Code</h2>
          </div>

          {qrData && (
            <div className="space-y-6">
              {/* QR Code Display */}
              <div className="flex flex-col items-center justify-center bg-white p-8 rounded-lg border-2 border-stone-200">
                <img
                  src={`data:image/png;base64,${qrData.qr_code}`}
                  alt="Quick Sign-In QR Code"
                  className="w-64 h-64"
                />
                <p className="text-sm text-stone-600 mt-4 text-center">
                  Scan this code to quickly capture lead information
                </p>
              </div>

              {/* URL Display */}
              <div>
                <label className="text-sm font-medium text-stone-700 mb-2 block">Sign-In URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={qrData.quick_signin_url}
                    readOnly
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-lg bg-stone-50 text-sm font-mono"
                  />
                  <Button
                    onClick={() => copyToClipboard(qrData.quick_signin_url)}
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                  >
                    <Copy size={16} className="mr-2" />
                    Copy
                  </Button>
                  <Button
                    onClick={() => window.open(qrData.quick_signin_url, '_blank')}
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Preview
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={downloadQRCode}
                  className="flex-1 bg-[#1A4D2E] hover:bg-[#15401F]"
                >
                  Download QR Code
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Usage Tips */}
        <Card className="p-6">
          <h3 className="font-semibold text-stone-900 mb-3">Usage Tips</h3>
          <ul className="space-y-2 text-sm text-stone-700">
            <li className="flex gap-2">
              <span className="text-[#1A4D2E] font-bold">•</span>
              <span>Print the QR code and place it at your office reception or networking events</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#1A4D2E] font-bold">•</span>
              <span>Share the URL via email or text message for quick lead capture</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#1A4D2E] font-bold">•</span>
              <span>All leads captured via Quick Sign-In are automatically marked as private</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#1A4D2E] font-bold">•</span>
              <span>Leads will appear in your regular Leads dashboard alongside other leads</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default QuickSignInSettings;
